import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";
import Images from "../../utils/Images";

const Testimonials = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const testimonials = [
    {
      id: 1,
      name: "James",
      role: "Fidelio Savings",
      logo: Images.testimonial_four,
      quote:
        "Fidelio Savings is a micro-finance organization; therefore, we needed to keep our customers updated about the transactions happening in their accounts. We wanted them to be sure that when they transferred money into their account, it did not end up somewhere else. To make all this possible, we started using TXT Connect bulk SMS in 2019. We have been able to keep our customers updated and are satisfied with the TXT Connect Bulk SMS services.",
    },
    {
      id: 2,
      name: "Sarah Johnson",
      role: "Marketing Director, Reviv Ghana",
      logo: Images.testimonial_two,
      quote:
        "TXTConnect has revolutionized how we communicate with our customers. The personalized messaging capabilities have increased our engagement rates by 40% and our conversion rates have never been higher. Their platform is intuitive and the customer support team is always responsive.",
    },
    {
      id: 3,
      name: "Michael Addo",
      role: "Operations Manager, CIMAF",
      logo: Images.testimonial_three,
      quote:
        "We needed a reliable way to send critical updates to our team members across multiple sites. TXTConnect's bulk SMS service has been a game-changer for our internal communications. The delivery rates are impressive and the scheduling feature saves us valuable time.",
    },
    {
      id: 4,
      name: "Daniel Lee",
      role: "General Manager, Tang Palace",
      logo: Images.testimonial_one,
      quote:
        "Since implementing TXTConnect's messaging system, our customer engagement has increased dramatically. The platform's reliability and ease of use have made it an essential part of our communication strategy. Their analytics tools have given us valuable insights into customer behavior.",
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 8000);

    return () => clearInterval(interval);
  }, [testimonials.length]);

  const handlePrev = () => {
    setCurrentTestimonial((prev) =>
      prev === 0 ? testimonials.length - 1 : prev - 1
    );
  };

  const handleNext = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  return (
    <section className="py-28 bg-gradient-to-b from-gray-50 via-white to-red-50 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-2/3 h-2/3 bg-red-50 rounded-full mix-blend-multiply filter blur-3xl opacity-60 transform translate-x-1/4 -translate-y-1/4"></div>
        <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-yellow-50 rounded-full mix-blend-multiply filter blur-3xl opacity-60 transform -translate-x-1/4 translate-y-1/4"></div>
      </div>

      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-red-600 mb-5">
            <span className="inline-block border-b-4 border-red-400 pb-2">
              Our Trusted Clients
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mt-4">
            Discover how businesses are transforming their communications with
            TXTConnect
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Testimonial Content */}
          <div className="lg:col-span-7 relative">
            <motion.div
              key={testimonials[currentTestimonial].id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-2xl shadow-xl p-10 relative z-10 overflow-hidden"
              style={{ boxShadow: "0 10px 40px rgba(0, 0, 0, 0.08)" }}
            >
              <div className="absolute -top-6 -left-6 text-red-100">
                <Quote size={100} strokeWidth={1} />
              </div>

              <div className="relative z-10">
                <p className="text-gray-700 text-lg md:text-xl leading-relaxed mb-12 italic">
                  "{testimonials[currentTestimonial].quote}"
                </p>

                <div className="border-t border-gray-100 pt-6 flex justify-between items-center">
                  <div>
                    <h4 className="font-bold text-xl text-gray-900">
                      {testimonials[currentTestimonial].name}
                    </h4>
                    <p className="text-gray-600 mt-1">
                      {testimonials[currentTestimonial].role}
                    </p>
                  </div>

                  <div className="flex space-x-3">
                    <button
                      onClick={handlePrev}
                      className="p-2.5 rounded-full bg-gray-100 hover:bg-red-100 text-red-600 transition-all hover:shadow-md"
                      aria-label="Previous testimonial"
                    >
                      <ChevronLeft size={20} />
                    </button>

                    <button
                      onClick={handleNext}
                      className="p-2.5 rounded-full bg-red-100 hover:bg-red-200 text-red-600 transition-all hover:shadow-md"
                      aria-label="Next testimonial"
                    >
                      <ChevronRight size={20} />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Indicators */}
            <div className="flex justify-center mt-8 space-x-3">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`transition-all duration-300 ${
                    currentTestimonial === index
                      ? "w-10 h-3 bg-red-500 rounded-full"
                      : "w-3 h-3 bg-gray-300 rounded-full hover:bg-gray-400"
                  }`}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>
          </div>

          {/* Customer Logo/Image */}
          <div className="lg:col-span-5 flex justify-center">
            <motion.div
              key={`logo-${testimonials[currentTestimonial].id}`}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="w-full"
            >
              <div
                className="bg-white rounded-2xl shadow-lg p-10 w-full max-w-md aspect-[4/3] flex items-center justify-center mx-auto"
                style={{ boxShadow: "0 10px 40px rgba(0, 0, 0, 0.06)" }}
              >
                <img
                  src={testimonials[currentTestimonial].logo}
                  alt={`${testimonials[currentTestimonial].role} logo`}
                  className="max-h-40 w-auto object-contain transform hover:scale-105 transition-transform duration-300"
                />
              </div>

              <div className="text-center mt-8">
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.2 }}
                  className="inline-flex items-center justify-center bg-red-50 px-4 py-2 rounded-full"
                >
                  <span className="h-2 w-2 rounded-full bg-red-500 mr-2"></span>
                  <span className="text-red-700 font-medium">
                    Trusted Partner
                  </span>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
